import React from 'react';
import { motion } from 'framer-motion';
import { Edit2, Trash2, User, Mail, Phone, Crown, Star } from 'lucide-react';

interface StaffMember {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  title?: string;
  description?: string;
  profile_image_url?: string;
  specialties?: string[];
  settings?: {
    visible_in_public?: boolean;
    rest_time?: number;
    max_daily_appointments?: number | null;
  };
  created_at?: string;
}

interface StaffCardProps {
  member: StaffMember;
  onEdit: (member: StaffMember) => void;
  onDelete: (memberId: string) => void;
}

export function StaffCard({ member, onEdit, onDelete }: StaffCardProps) {
  const isAdmin = member.role === 'admin';
  const isActive = member.settings?.visible_in_public !== false;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group bg-white rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300 overflow-hidden"
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="relative flex-shrink-0">
            {member.profile_image_url ? (
              <img
                src={member.profile_image_url}
                alt={member.name}
                className="w-16 h-16 rounded-2xl object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <User className="h-7 w-7 text-white" />
              </div>
            )}
            {isAdmin && (
              <div className="absolute -top-1 -right-1 bg-amber-400 rounded-full p-1 shadow-md">
                <Crown className="h-3.5 w-3.5 text-white" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-gray-900 text-lg truncate">{member.name}</h3>
              {isAdmin && (
                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[11px] font-semibold rounded-full">
                  מנהל
                </span>
              )}
            </div>

            {member.title && (
              <p className="text-sm text-gray-600 mb-2">{member.title}</p>
            )}

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{member.email}</span>
              </div>
              {member.phone && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{member.phone}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => onEdit(member)}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 active:scale-95 transition-all rounded-xl"
              title="עריכה"
            >
              <Edit2 className="h-4 w-4" />
            </button>
            {!isAdmin && (
              <button
                onClick={() => onDelete(member.id)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 active:scale-95 transition-all rounded-xl"
                title="מחיקה"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {member.specialties && member.specialties.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {member.specialties.slice(0, 3).map((specialty, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700"
              >
                <Star className="h-3 w-3" />
                {specialty.length > 15 ? `${specialty.slice(0, 15)}...` : specialty}
              </span>
            ))}
            {member.specialties.length > 3 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-600">
                +{member.specialties.length - 3}
              </span>
            )}
          </div>
        )}

        {member.description && (
          <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-2">
            {member.description}
          </p>
        )}

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <div
              className={`w-2 h-2 rounded-full ${
                isActive ? 'bg-green-500' : 'bg-gray-400'
              }`}
            />
            <span
              className={`text-xs font-medium ${
                isActive ? 'text-green-700' : 'text-gray-500'
              }`}
            >
              {isActive ? 'גלוי בדף הזמנות' : 'מוסתר מדף הזמנות'}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
